import React, {FC, useState, useEffect} from "react"
import styled from "styled-components"
import {Row} from "react-bootstrap"

import {Card} from "@irati/ui/src/Layout"
import {Body, H1, H2, H3, Label} from "@irati/ui/src/Typography"
import Button from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import ToastAlert from "@irati/ui/src/ToastAlert"
import {triggerSuccessToast} from "@irati/ui/src/ToastAlert/triggerToast"
import {useTheme} from "@irati/ui/src/global/useTheme"
import GenericModal from "@irati/ui/src/GenericModal"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import ga4Tracking from "@irati-web/tracking"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import {useUpdateEnquiry} from "@irati/signup/hooks"
import {SignUp} from "@irati/types"
import {UseQueriesResult} from "@irati/utils/services/apollo"

import SaveAndProgressFooter from "../../components/SignUp/SaveAndProgressFooter"
import TextWithTick from "../../components/SignUp/TextWithTick"
import SelfSignUpFooter from "../../components/SignUp/Footer"
import {DesktopOnlyWrapper, MobileOnlyWrapper, StyledCard, StyledStepCard} from "../../components/SignUp/styled"
import EditDetailsModal, {SBSUser} from "../../components/SignUp/EditDetailsModal"

interface StepCardProps {
  number: number
  text: string
  image: string
  alt: string
}

const StepCard: FC<StepCardProps> = ({number, text, image, alt}) => {
  return (
    <StyledStepCard padding={1}>
      <Flex direction="column" alignItems="center">
        <img src={image} height={60} width={60} alt={alt}></img>
        <H3 marginTop={0.5}>
          <FormattedMessage id="platform.selfSignUp.welcome.stepNo" values={{number}} />
        </H3>
        <Label>{text}</Label>
      </Flex>
    </StyledStepCard>
  )
}

const ContentWrapper = styled(Flex)`
  ${({theme}) => theme.mediaQueries.maxWidthMedia.desktop`
    flex-direction: column-reverse;
  `}
`

const FlexStep = styled(Flex)`
  ${({theme}) => theme.mediaQueries.maxWidthMedia.desktop`
    flex-direction: column;
    align-items: center;
  `}
`

const FlexInput = styled(Flex)`
  ${({theme}) => theme.mediaQueries.maxWidthMedia.desktop`
    flex-direction: column;
  `}
`

const EditDetailsWebButton = styled(Button)`
  ${({theme}) => theme.mediaQueries.maxWidthMedia.desktop`
    display: none;
  `}
`

export const WelcomeMobileButton = styled(Button)`
  ${({theme}) => theme.mediaQueries.minWidthMedia.desktop`
    display: none;
  `}
`

const StartSetupMobileButton = styled(Button)`
  ${({theme}) => theme.mediaQueries.minWidthMedia.desktop`
    display: none;
  `}
`

const FullRow = styled(Row)`
  width: 110%;
`

export const StyledModal = styled(GenericModal)`
  ${({theme}) => theme.mediaQueries.maxWidthMedia.phoneLandscape`
    .modal-dialog {
      position: fixed;
      bottom: 0px;
  `}
`

interface Props {
  user: SBSUser
  onClick: () => void
  signUp: UseQueriesResult<SignUp>
}

const SelfSignUpWelcome: FC<Props> = ({user, onClick, signUp}) => {
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false)
  const [firstName, setFirstName] = useState(user.firstName)
  const [middleName, setMiddleName] = useState(null)
  const [lastName, setLastName] = useState(user.lastName)
  const [emailAddress, setEmailAddress] = useState(user.emailAddress)
  const {formatMessage} = useIntl()
  const theme = useTheme()

  const handleStartSetup = () => {
    onClick()
    ga4Tracking.trackEventWithStore("start_setup", "sbs_params", {step: "1.2", page: "welcome_page"})
  }
  const handleEditDetails = () => {
    setShowEditDetailsModal(true)
  }

  useEffect(() => {
    ga4Tracking.trackEventWithStore("welcome_page_viewed", "sbs_params", {
      step: "1",
      page: "welcome_page"
    })
  }, [])

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      triggerSuccessToast(formatMessage({id: "platform.selfSignUp.welcome.copyLinkMessage"}))
    })
  }

  const [updateEnquiry] = useUpdateEnquiry({
    variables: {
      uuid: signUp.data.uuid,
      enquiry: {
        ...signUp.data.enquiry,
        firstName,
        lastName,
        middleName,
        emailAddress
      }
    }
  })

  const handleUpdateInformation = (updatedUser: SBSUser) => {
    ga4Tracking.trackEventWithStore("details_updated", "sbs_params", {step: "1.1", page: "welcome_page", company: user.companyName})
    setFirstName(updatedUser.firstName)
    setMiddleName(updatedUser.middleName)
    setLastName(updatedUser.lastName)
    setEmailAddress(updatedUser.emailAddress)
    setShowEditDetailsModal(false)
    updateEnquiry()
  }

  return (
    <>
      <ToastAlert />
      <StyledCard>
        <Flex justifyContent="space-between" alignItems="center">
          <H1>
            <FormattedMessage id="platform.selfSignUp.welcome.title" values={{firstName}} /> 👋
          </H1>
          <EditDetailsWebButton tertiary onClick={handleEditDetails}>
            <FormattedMessage id="platform.selfSignUp.welcome.editDetailsButton" />
            <Icon icon={IconTypes.edit} size={16} marginLeft />
          </EditDetailsWebButton>
        </Flex>
        <MobileOnlyWrapper>
          <Body large maxWidth="100%" marginTop={2} marginBottom={3}>
            <FormattedMessage id="platform.selfSignUp.welcome.subTitle" />
          </Body>
        </MobileOnlyWrapper>
        <DesktopOnlyWrapper>
          <Body large maxWidth="100%" marginTop={2}>
            <FormattedMessage id="platform.selfSignUp.welcome.subTitle" />
          </Body>
          <Body large maxWidth="100%" marginBottom={3}>
            <FormattedMessage id="platform.selfSignUp.welcome.subTitle2" />
          </Body>
        </DesktopOnlyWrapper>
        <StartSetupMobileButton primary full marginBottom={3} onClick={handleStartSetup}>
          <FormattedMessage id="platform.selfSignUp.welcome.popUp.startSetup" />
        </StartSetupMobileButton>
        <WelcomeMobileButton tertiary full marginBottom={3} onClick={handleEditDetails}>
          <Icon icon={IconTypes.edit} size={16} marginRight />
          <FormattedMessage id="platform.selfSignUp.welcome.editDetailsButton" />
        </WelcomeMobileButton>
        <WelcomeMobileButton tertiary full marginBottom={3} onClick={handleCopyLink}>
          <Icon icon={IconTypes.files} size={16} marginRight />
          <FormattedMessage id="platform.selfSignUp.welcome.popUp.copyLink" />
        </WelcomeMobileButton>
        <ContentWrapper gap={4}>
          <div>
            <FlexStep gap={3} marginBottom={2}>
              <StepCard
                number={1}
                text={formatMessage({id: "platform.selfSignUp.welcome.step1.subTitle"})}
                image={formatMessage({id: "platform.selfSignUp.welcome.step1.img"})}
                alt="confirmYourPlan"
              />
              <StepCard
                number={2}
                text={formatMessage({id: "platform.selfSignUp.welcome.step2.subTitle"})}
                image={formatMessage({id: "platform.selfSignUp.welcome.step2.img"})}
                alt="verifyCompany"
              />
            </FlexStep>
            <FlexStep gap={3}>
              <StepCard
                number={3}
                text={formatMessage({id: "platform.selfSignUp.welcome.step3.subTitle"})}
                image={formatMessage({id: "platform.selfSignUp.welcome.step3.img"})}
                alt="verifyIdentity"
              />
              <StepCard
                number={4}
                text={formatMessage({id: "platform.selfSignUp.welcome.step4.subTitle"})}
                image={formatMessage({id: "platform.selfSignUp.welcome.step4.img"})}
                alt="completePayment"
              />
            </FlexStep>
          </div>
          <div>
            <Card bgColor={theme.colors.secondary1_50} marginBottom={2}>
              <H2 color={theme.colors.primary2} marginBottom={1}>
                <FormattedMessage id="platform.selfSignUp.welcome.infoCheck.title" />
              </H2>
              <TextWithTick text={formatMessage({id: "platform.selfSignUp.welcome.infoCheck.subtitle1"})} />
              <TextWithTick text={formatMessage({id: "platform.selfSignUp.welcome.infoCheck.subtitle2"})} />
              <TextWithTick text={formatMessage({id: "platform.selfSignUp.welcome.infoCheck.subtitle3"})} />
              <TextWithTick text={formatMessage({id: "platform.selfSignUp.welcome.infoCheck.subtitle4"})} />
            </Card>
          </div>
        </ContentWrapper>
      </StyledCard>
      {showEditDetailsModal && (
        <EditDetailsModal
          user={{firstName, lastName, middleName, emailAddress, companyName: user.companyName}}
          show
          onHide={() => setShowEditDetailsModal(false)}
          onConfirm={handleUpdateInformation}
        />
      )}

      <SaveAndProgressFooter
        onConfirm={handleStartSetup}
        confirmText={formatMessage({id: "platform.selfSignUp.welcome.popUp.startSetup"})}
      />
      <SelfSignUpFooter />
    </>
  )
}

export default SelfSignUpWelcome
